import { useEffect, useRef, useState } from 'react';
import { BarChart3, ImagePlus, Save } from 'lucide-react';
import { chPostService } from '../services/postService';
import { chPrintService } from '../services/printService';
import { useModal } from '../../contexts/ModalContext';
import { PrintsGallery } from './PrintsGallery';
import { isVideoUrl } from './MediaPreview';
import {
  METRIC_FIELDS,
  normalizeMetrics,
  combineMetrics,
  getEngagement,
  getEngagementRate,
  hasInteractions,
  summarizeMetrics,
  formatCompactNumber,
  formatDuration,
  formatPercent,
  parseDuration,
  type ChMetricKey,
  type ChPostMetrics,
} from '../lib/metrics';
import { getMediaUrls, getPlatforms, VIDEO_CONTENT_TYPES } from '../lib/posts';
import { PLATFORM_LABELS, type ChPlatformMetrics, type ChPost, type ChPostPlatform, type ChPostPrint } from '../types';

interface Props {
  post: ChPost;
  onPostUpdated: (post: ChPost) => void;
}

type Draft = Partial<Record<ChMetricKey, string>>;
type Drafts = Partial<Record<ChPostPlatform, Draft>>;

function toDraft(metrics: ChPostMetrics | null | undefined): Draft {
  const m = normalizeMetrics(metrics);
  const draft: Draft = {};
  for (const { key, kind } of METRIC_FIELDS) {
    if (m[key] === undefined) continue;
    draft[key] = kind === 'duration'
      ? String(m[key]).replace('.', ',')
      : String(m[key]);
  }
  return draft;
}

/** Per platform when the item went to several; the stored combined set otherwise. */
function toDrafts(post: ChPost): Drafts {
  const platforms = getPlatforms(post);
  if (platforms.length === 1) return { [platforms[0]]: toDraft(post.metrics) };
  const byPlatform = post.metrics_by_platform || {};
  const hasSplit = platforms.some(p => byPlatform[p]);
  const drafts: Drafts = {};
  platforms.forEach((p, i) => {
    // Numbers typed before the item had several platforms go to the first one
    drafts[p] = toDraft(hasSplit ? byPlatform[p] : i === 0 ? post.metrics : null);
  });
  return drafts;
}

// Whole numbers only for counts: "1.200" / "1 200" / "1,200" all mean 1200;
// watch time takes "0:45", "45" or "8,2"
function parseDraft(draft: Draft | undefined): ChPostMetrics {
  const raw: Record<string, unknown> = {};
  for (const { key, kind } of METRIC_FIELDS) {
    const value = (draft?.[key] ?? '').trim();
    if (!value) continue;
    raw[key] = kind === 'duration' ? parseDuration(value) : value.replace(/[\s.,]/g, '');
  }
  return normalizeMetrics(raw);
}

/** ISO → value for <input type="datetime-local"> in local time. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Stats typed by the team and screenshots of the platform insights. */
export function PostStatsPanel({ post, onPostUpdated }: Props) {
  const { toast, showConfirm } = useModal();
  const fileRef = useRef<HTMLInputElement>(null);
  const platforms = getPlatforms(post);
  const [drafts, setDrafts] = useState<Drafts>(() => toDrafts(post));
  const [tab, setTab] = useState<ChPostPlatform>(platforms[0]);
  const [publishedAt, setPublishedAt] = useState(() => toLocalInput(post.published_at));
  const [saving, setSaving] = useState(false);
  const [prints, setPrints] = useState<ChPostPrint[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setDrafts(toDrafts(post));
    setTab(getPlatforms(post)[0]);
    setPublishedAt(toLocalInput(post.published_at));
    chPrintService.listByPost(post.id)
      .then(setPrints)
      .catch(() => setPrints([]));
    // Reset only when switching items (or their platforms), not on every save
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id, platforms.join()]);

  const multi = platforms.length > 1;
  const activeTab = platforms.includes(tab) ? tab : platforms[0];
  const parsedByPlatform = Object.fromEntries(platforms.map(p => [p, parseDraft(drafts[p])])) as Record<ChPostPlatform, ChPostMetrics>;
  const parsed = multi ? combineMetrics(platforms.map(p => parsedByPlatform[p])) : parsedByPlatform[platforms[0]];
  const current = parsedByPlatform[activeTab];
  const isVideo = VIDEO_CONTENT_TYPES.includes(post.content_type) || getMediaUrls(post).some(isVideoUrl);
  const fields = METRIC_FIELDS.filter(f => !f.videoOnly || isVideo || current[f.key] !== undefined);

  const savedDrafts = toDrafts(post);
  const dirty = platforms.some(p => JSON.stringify(parseDraft(drafts[p])) !== JSON.stringify(parseDraft(savedDrafts[p])))
    || publishedAt !== toLocalInput(post.published_at);
  const rate = getEngagementRate(current);
  const overall = multi ? summarizeMetrics([platforms.map(p => parsedByPlatform[p])]) : null;

  async function handleSave() {
    setSaving(true);
    try {
      const byPlatform: ChPlatformMetrics | null = multi
        ? Object.fromEntries(platforms
          .filter(p => Object.keys(parsedByPlatform[p]).length > 0)
          .map(p => [p, parsedByPlatform[p]]))
        : null;
      const published = publishedAt ? new Date(publishedAt).toISOString() : null;
      // Before the v2 SQL only the combined numbers can be stored
      const extra = multi || published || post.published_at || post.metrics_by_platform
        ? { metrics_by_platform: byPlatform, published_at: published }
        : {};
      const updated = await chPostService.updateMetrics(post.id, parsed, extra);
      onPostUpdated(updated);
      toast.success('Stats guardadas');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Não foi possível guardar as stats', 8000);
    } finally {
      setSaving(false);
    }
  }

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploading(true);
    let position = prints.length;
    const added: ChPostPrint[] = [];
    try {
      for (const file of files) {
        added.push(await chPrintService.upload(post.id, post.project_id, file, position++));
      }
      toast.success(files.length === 1 ? 'Print adicionado' : `${files.length} prints adicionados`);
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Não foi possível carregar o print', 8000);
    } finally {
      if (added.length) setPrints(prev => [...prev, ...added]);
      setUploading(false);
    }
  }

  async function handleRemove(print: ChPostPrint) {
    const confirmed = await showConfirm({
      title: 'Remover print?',
      message: 'O print deixa de aparecer para a equipa e para o cliente.',
      confirmText: 'Remover',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await chPrintService.remove(print.id);
      setPrints(prev => prev.filter(p => p.id !== print.id));
    } catch {
      toast.error('Não foi possível remover o print');
    }
  }

  return (
    <div className="ch-card space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-[var(--ch-ink)] flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Stats
        </h3>
        {post.metrics_updated_at && (
          <span className="text-[10px] text-gray-400">
            Atualizado a {new Date(post.metrics_updated_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        )}
      </div>

      {multi && (
        <div className="inline-flex items-center bg-gray-100 rounded-full p-0.5" role="tablist" aria-label="Plataforma">
          {platforms.map(p => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={activeTab === p}
              onClick={() => setTab(p)}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                activeTab === p ? 'bg-[var(--ch-ink)] text-white' : 'text-gray-500 hover:text-[var(--ch-ink)]'
              }`}
            >
              {PLATFORM_LABELS[p]}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {fields.map(f => (
          <label key={f.key} className="block">
            <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 line-clamp-1" title={f.label}>{f.label}</span>
            <input
              type="text"
              inputMode={f.kind === 'duration' ? 'decimal' : 'numeric'}
              className="ch-input h-9 text-sm tabular-nums mt-0.5"
              value={drafts[activeTab]?.[f.key] ?? ''}
              placeholder={f.kind === 'duration' ? 'ex. 0:45' : '—'}
              onChange={e => setDrafts(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], [f.key]: e.target.value } }))}
            />
          </label>
        ))}
        <div className="block">
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Interações</span>
          <div className="h-9 mt-0.5 px-3 flex items-center rounded-xl bg-gray-50 text-sm font-semibold tabular-nums text-[var(--ch-ink)]" title="Gostos + comentários + partilhas + guardados">
            {hasInteractions(current) ? getEngagement(current).toLocaleString('pt-PT') : '—'}
          </div>
        </div>
      </div>

      <label className="block max-w-xs">
        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Publicado a (data e hora)</span>
        <input
          type="datetime-local"
          className="ch-input h-9 text-sm mt-0.5"
          value={publishedAt}
          onChange={e => setPublishedAt(e.target.value)}
        />
      </label>

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500 space-y-0.5">
          <p>
            {rate !== null
              ? <>Taxa de envolvimento{multi ? ` (${PLATFORM_LABELS[activeTab]})` : ''}: <strong className="text-[var(--ch-ink)]">{formatPercent(rate)}</strong></>
              : 'Preenche só o que a plataforma mostra.'}
          </p>
          {overall && overall.itemsWithStats > 0 && (
            <p>
              Total:{' '}
              {overall.totals.views !== undefined && <>{formatCompactNumber(overall.totals.views)} visualizações · </>}
              {formatCompactNumber(overall.engagement)} interações
              {overall.engagementRate !== null && <> · <strong className="text-[var(--ch-ink)]">{formatPercent(overall.engagementRate)}</strong></>}
              {overall.totals.avg_watch_time !== undefined && <> · {formatDuration(overall.totals.avg_watch_time)} em média</>}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="ch-btn ch-btn-primary ch-btn-sm disabled:opacity-50 flex-shrink-0"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'A guardar...' : 'Guardar stats'}
        </button>
      </div>

      <div className="pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold text-[var(--ch-ink)]">Prints ({prints.length})</h4>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="ch-btn ch-btn-ghost ch-btn-sm"
          >
            <ImagePlus className="w-3.5 h-3.5" />
            {uploading ? 'A carregar...' : 'Adicionar'}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
        </div>
        {prints.length === 0 ? (
          <p className="text-xs text-gray-400">Carrega screenshots das estatísticas (insights) da publicação.</p>
        ) : (
          <PrintsGallery prints={prints} onRemove={handleRemove} />
        )}
      </div>
    </div>
  );
}
